import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, MailCheck, ShieldCheck } from "lucide-react";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import {
  isApiConfigured,
  postVerifyTurnstileToken,
  postVerifyEmail,
  postResendVerificationEmail,
  getVerificationEmailStatus,
} from "../../apiClient";
import { buildAuthHrefWithNext, parseSafeInternalNextPath } from "../../navigateAfterAuth";

function old1_VerifyEmailPage({ onSignIn, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signupData = location.state?.signupData || null;
  const emailParam = searchParams.get("email") || signupData?.email || "";
  const safeNext = useMemo(() => parseSafeInternalNextPath(searchParams.get("next")), [searchParams]);
  const verifyMobilePath = useMemo(
    () => (safeNext ? `/old1/verify-mobile?next=${encodeURIComponent(safeNext)}` : "/old1/verify-mobile"),
    [safeNext]
  );
  const signinHref = useMemo(() => buildAuthHrefWithNext("/old1/signin", safeNext), [safeNext]);
  const signupHref = useMemo(() => buildAuthHrefWithNext("/old1/signup", safeNext), [safeNext]);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  /** Bumped on every widget reset so late Turnstile callbacks from an old iframe cannot overwrite the token. */
  const [turnstileSession, setTurnstileSession] = useState(0);
  const turnstileSessionRef = useRef(0);
  const [resendStatus, setResendStatus] = useState("");
  const [busy, setBusy] = useState(false);
  /** Generic “contact support” panel (email send limit — reusable pattern). */
  const [showContactSupport, setShowContactSupport] = useState(false);
  const inputsRef = useRef([]);

  const displayEmail = String(emailParam || "").trim() || "your email";

  useEffect(() => {
    if (!isApiConfigured()) return;
    const e = String(emailParam || "").trim().toLowerCase();
    if (!e) return;
    let cancelled = false;
    (async () => {
      const status = await getVerificationEmailStatus(e);
      if (cancelled || !status) return;
      if (status.exhausted && status.userFound && !status.emailVerified) {
        setShowContactSupport(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [emailParam]);

  const updateDigit = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError("");

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setError("");
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const runHumanCheckIfNeeded = async () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    if (!turnstileToken) {
      throw new Error(TURNSTILE_REQUIRED_MESSAGE);
    }
    await postVerifyTurnstileToken(turnstileToken);
  };

  /** Turnstile tokens are single-use (server verifies before checking the code). Bump session so stale iframe callbacks are ignored. */
  const bumpTurnstileSession = () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    turnstileSessionRef.current += 1;
    setTurnstileSession(turnstileSessionRef.current);
    setTurnstileKey((k) => k + 1);
    setTurnstileToken(null);
  };

  const handleTurnstileTokenChange = (token, widgetSessionId) => {
    if (typeof widgetSessionId === "number" && widgetSessionId !== turnstileSessionRef.current) {
      return;
    }
    setTurnstileToken(token);
    if (token) {
      setError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) {
      setError("Enter the full 6-digit code.");
      return;
    }

    const normalizedEmail = String(emailParam || "").trim().toLowerCase();
    if (isApiConfigured() && !normalizedEmail) {
      setError("Missing email. Go back to sign up and try again.");
      return;
    }

    setError("");
    setResendStatus("");
    setBusy(true);
    try {
      if (isApiConfigured()) {
        if (isTurnstileEnabled() && !turnstileToken) {
          throw new Error(TURNSTILE_REQUIRED_MESSAGE);
        }
        const turnstilePayload =
          isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
        const { user } = await postVerifyEmail({
          email: normalizedEmail,
          code,
          turnstileToken: turnstilePayload,
          authToken,
        });
        if (typeof onSignIn === "function") {
          onSignIn(user);
        }
        const base = signupData || {};
        const nextSignupData = {
          ...base,
          emailVerified: true,
          email: user.email || base.email || normalizedEmail,
          fullName: user.name ?? base.fullName,
          signupType: user.role === "hiring-manager" ? "hiring-manager" : "applicant",
          countryCode: user.countryCode ?? base.countryCode,
          mobile: user.mobile ?? base.mobile,
          password: base.password,
        };
        navigate(verifyMobilePath, {
          state: { signupData: nextSignupData },
        });
      } else {
        await runHumanCheckIfNeeded();
        const base = signupData || {};
        const nextSignupData = {
          ...base,
          emailVerified: true,
          email: base.email || normalizedEmail,
          fullName: base.fullName,
          signupType: base.signupType || "applicant",
          countryCode: base.countryCode,
          mobile: base.mobile,
          password: base.password,
        };
        navigate(verifyMobilePath, {
          state: { signupData: nextSignupData },
        });
      }
    } catch (err) {
      const code = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (code === "VERIFICATION_EMAILS_EXHAUSTED") {
        setShowContactSupport(true);
        setError("");
      } else {
        const raw = err instanceof Error ? err.message : "Verification failed";
        setError(formatTurnstileErrorForUser(raw));
        bumpTurnstileSession();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setResendStatus("");
    setBusy(true);
    try {
      const normalizedEmail = String(emailParam || "").trim().toLowerCase();
      if (isApiConfigured()) {
        if (!normalizedEmail) {
          throw new Error("Missing email. Go back to sign up.");
        }
        if (isTurnstileEnabled() && !turnstileToken) {
          throw new Error(TURNSTILE_REQUIRED_MESSAGE);
        }
        const turnstilePayload =
          isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
        await postResendVerificationEmail({
          email: normalizedEmail,
          turnstileToken: turnstilePayload,
          authToken,
        });
        setResendStatus(
          "We sent a new code to your email. Complete the security check again below, then verify."
        );
      } else {
        await runHumanCheckIfNeeded();
        setResendStatus("We sent you a new code. Is your email correct?");
      }
      bumpTurnstileSession();
    } catch (err) {
      const code = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (code === "VERIFICATION_EMAILS_EXHAUSTED") {
        setShowContactSupport(true);
        setError("");
      } else {
        const raw = err instanceof Error ? err.message : "Verification failed";
        setError(formatTurnstileErrorForUser(raw));
        bumpTurnstileSession();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Verify your email"
        description="Confirm your email address to activate your JOBUWIN account."
        path="/old1/verify-email"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Email verification navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button type="button" className="jw-public-button jw-public-button--text landing-nav-login" onClick={() => navigate(signinHref)}>
              Sign in
            </button>
            <button type="button" className="jw-public-button jw-public-button--primary landing-nav-cta" onClick={() => navigate(signupHref)}>
              Sign up
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        <aside className="jw-auth-aside" aria-label="Email verification guidance">
          <span className="jw-public-chip">
            <ShieldCheck className="jw-public-icon" aria-hidden="true" />
            Verify your email
          </span>
          <h1>One code keeps your JOBUWIN account trusted.</h1>
          <p>
            Confirming your email helps protect your account and keeps the applicant and hiring flows genuine.
          </p>
          <div className="jw-auth-benefits">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Six-digit verification code</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Resend support if the email does not arrive</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Mobile verification follows next</span>
          </div>
        </aside>

        <section className="signup-card jw-public-card jw-auth-card">
          <Link
            className="back-link jw-public-back-link"
            to={showContactSupport ? "/home" : signupHref}
            state={showContactSupport ? undefined : signupData ? { signupData } : undefined}
          >
            <ArrowLeft className="jw-public-icon" aria-hidden="true" />
            {showContactSupport ? "Back to home" : "Back to sign up"}
          </Link>
          <p className="eyebrow jw-public-eyebrow">Email verification</p>
          <h2 className={showContactSupport ? "help-contact-heading" : undefined}>
            {showContactSupport
              ? "We are sorry. It looks like the system is not working correctly for you."
              : "Verify your email"}
          </h2>
          {!showContactSupport ? (
            <>
          <p className="subtitle">
            We sent a 6-digit code to <strong>{displayEmail}</strong>. Enter it below to continue.
          </p>
          <p className="change-email-wrap">
            Wrong email?{" "}
            <Link
              to={signupHref}
              className="change-email-link"
              state={signupData ? { signupData } : undefined}
            >
              Change email
            </Link>
          </p>
            </>
          ) : null}

          {showContactSupport ? (
            <div className="help-contact-panel" role="alert">
              <p className="help-contact-panel__text">
                Please email{" "}
                <a className="help-contact-panel__link" href="mailto:help@jobuwin.com">
                  help@jobuwin.com
                </a>{" "}
                and we&apos;ll help you with the next steps.
              </p>
              <p className="help-contact-panel__footer">
                <Link className="change-email-link" to="/old1/home">
                  Back to home
                </Link>
              </p>
            </div>
          ) : null}

          {!showContactSupport ? (
            <>
          <form className="signup-form jw-auth-form jw-auth-code-form" onSubmit={onSubmit}>
            <label htmlFor="code-0">Verification code</label>
            <div className="jw-auth-code-heading">
              <MailCheck className="jw-public-icon" aria-hidden="true" />
              Enter the email code
            </div>
            <div className="code-inputs" onPaste={handlePaste}>
              {digits.map((digit, index) => (
                <input
                  key={`digit-${index}`}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                  ref={(element) => {
                    inputsRef.current[index] = element;
                  }}
                  aria-label={`Verification digit ${index + 1}`}
                  required
                />
              ))}
            </div>

            {error && error !== TURNSTILE_REQUIRED_MESSAGE ? (
              <p
                className="field-note"
                role="alert"
                style={{ color: "#b42318", whiteSpace: "pre-line" }}
              >
                {error}
              </p>
            ) : null}

            {isTurnstileEnabled() && isApiConfigured() ? (
              <>
                {error === TURNSTILE_REQUIRED_MESSAGE ? (
                  <p className="turnstile-inline-alert" role="alert">
                    {error}
                  </p>
                ) : null}
                <TurnstileField
                  tokenSessionId={turnstileSession}
                  onTokenChange={handleTurnstileTokenChange}
                  widgetKey={`verify-email-${turnstileKey}`}
                />
                <p className="turnstile-human-note">
                  Security check: making sure you are a genuine human :-)
                </p>
              </>
            ) : null}

            <button type="submit" className="submit-button jw-public-button jw-public-button--primary" disabled={busy}>
              {busy ? "Please wait..." : "Verify email"}
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </form>

          <p className="verification-help">
            Didn&apos;t get it?{" "}
            <button type="button" className="change-email-link" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", textDecoration: "underline" }} onClick={handleResend} disabled={busy}>
              Resend code
            </button>
          </p>
          {resendStatus ? (
            <p
              className="field-note"
              style={{ marginTop: "1.25rem", textAlign: "center" }}
            >
              {resendStatus}
            </p>
          ) : null}
            </>
          ) : null}
        </section>
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_VerifyEmailPage;
